import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Box, SubTitle, Table, Icon } = vp;

type LinkedTransactionInfoProps = {
  LinkedTransactions: vp.LinkedTransactionsMap;
  Currency: string;
};

type FC = FunctionComponent<LinkedTransactionInfoProps>;

const LinkedTransactionInfo: FC = (props: LinkedTransactionInfoProps) => {
  const { LinkedTransactions, Currency } = props;
  const { accountId } = useParams<{ accountId: string }>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.LinkedTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const subTransactionsAvailable = LinkedTransactions && LinkedTransactions.length > 0;

  const tableProps: vp.TableProps = {
    name: 'account-billing-settings-list',
    columns: {
      transactionID: { header: t('transactionDetails.transactionID') || '' },
      transactionStatus: { header: t('transactionDetails.transactionStatus') || '' },
      transactionType: { header: t('transactionDetails.transactionType') || '' },
      errorMessage: { header: t('transactionDetails.errorMessage') || '' },
      timestamp: { header: t('transactionDetails.timestamp') || '' },
      view: { header: t('transactionDetails.view') || '' },
    },
    rows:
      LinkedTransactions?.map((subTransaction) => ({
        transactionID: subTransaction.TransactionID,
        transactionStatus: tGlobal(`Transaction.TransactionHistory.statuses.${subTransaction.TransactionStatus}`, {
          defaultValue: subTransaction.TransactionStatus,
        }),
        transactionType: tGlobal(`Transaction.TransactionHistory.transactionTypes.${subTransaction.TransactionType}`, {
          defaultValue: subTransaction.TransactionType,
        }),
        errorMessage: subTransaction.FailureReason,
        timestamp: subTransaction.Timestamp,
        view: shouldShowView(subTransaction) ? (
          <Link
            to={{
              pathname: `/transactions-history/details/${removeWhiteSpace(subTransaction)}/${
                subTransaction.TransactionID
              }/${Currency}`,
              state: {
                accountId,
                transactionDetail: {
                  TransactionID: subTransaction.TransactionID,
                  type: subTransaction.TransactionType,
                },
              },
            }}
          >
            <Box jc="center">
              <Icon icon="angleRight" size="xsm" />
            </Box>
          </Link>
        ) : (
          ''
        ),
      })) || [],
  };

  return (
    <div>
      {subTransactionsAvailable && (
        <Box mt="lg" mb="lg" flx="col">
          <SubTitle subTitle={t('linkedTransactions') || ''} />
        </Box>
      )}
      <Table {...tableProps} />
    </div>
  );

  function shouldShowView({ TransactionType }: vp.LinkedTransactions) {
    const isEFT = [vp.TransactionType.EFT_FUNDING, vp.TransactionType.EFT_WITHDRAWAL].includes(TransactionType);
    const isInteract = [vp.TransactionType.INTERACT_MONEY_REQUEST, vp.TransactionType.INTERACT_BULK_PAYOUT].includes(
      TransactionType,
    );
    const isCreditCard = [vp.TransactionType.CREDIT_CARD].includes(TransactionType);
    return isEFT || isInteract || isCreditCard;
  }

  function removeWhiteSpace({ TransactionType }: vp.LinkedTransactions) {
    return decodeURI(TransactionType).replaceAll(' ', '');
  }
};

export default LinkedTransactionInfo;
